import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cartLineKey, useStoreCart } from '../../context/StoreCartContext';

export function GoldenAmount({ amount, size = 'md', muted = false, className = '' }) {
  const sizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-xl',
    xl: 'text-3xl',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-black tabular-nums ${
        muted ? 'text-slate-500 dark:text-slate-400' : 'text-amber-600 dark:text-amber-400'
      } ${sizes[size] || sizes.md} ${className}`}
    >
      <span aria-hidden className="drop-shadow-sm">🪙</span>
      {Number(amount || 0).toLocaleString()}
      <span className="sr-only"> golden tickets</span>
    </span>
  );
}

export function StoreHeader({ goldenTickets, title = 'Golden Store', subtitle }) {
  const { itemCount } = useStoreCart();
  return (
    <div className="mb-8 overflow-hidden rounded-3xl border border-amber-200/80 dark:border-amber-500/20 bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-amber-950/30 dark:via-slate-900 dark:to-orange-950/20 shadow-sm">
      <div className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400 mb-2">
            WE R GAME rewards marketplace
          </p>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 dark:text-white">{title}</h1>
          {subtitle && (
            <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {goldenTickets != null && (
            <div className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-white/80 dark:bg-black/20 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Your balance
              </p>
              <GoldenAmount amount={goldenTickets} size="lg" />
            </div>
          )}
          <Link
            to="/store/cart"
            className="relative inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 dark:bg-white px-5 py-3.5 font-bold text-white dark:text-slate-950 hover:opacity-90"
          >
            <span aria-hidden>🛒</span>
            Cart
            {itemCount > 0 && (
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-black text-slate-950">
                {itemCount}
              </span>
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}

export function CartQuantityControls({
  quantity,
  onDecrease,
  onIncrease,
  disableIncrease = false,
  compact = false,
  className = '',
}) {
  const buttonSize = compact ? 'h-9 w-9 text-lg' : 'h-12 w-12 text-xl';
  return (
    <div
      className={`inline-flex items-center justify-between rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 p-1 ${className}`}
      aria-label="Cart quantity"
    >
      <button
        type="button"
        onClick={onDecrease}
        className={`${buttonSize} rounded-lg bg-white dark:bg-slate-900 font-black text-slate-800 dark:text-white shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800`}
        aria-label="Remove one from cart"
      >
        −
      </button>
      <span className={`${compact ? 'min-w-10 text-base' : 'min-w-14 text-lg'} px-2 text-center font-black tabular-nums text-slate-950 dark:text-white`}>
        {quantity}
      </span>
      <button
        type="button"
        onClick={onIncrease}
        disabled={disableIncrease}
        className={`${buttonSize} rounded-lg bg-amber-400 font-black text-slate-950 shadow-sm hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="Add one more to cart"
      >
        +
      </button>
    </div>
  );
}

export function ProductCard({ product }) {
  const { items, addItem, updateQuantity } = useStoreCart();
  const activeVariants = (product.variants || []).filter((variant) => variant.active);
  const firstAvailableVariant =
    activeVariants.find((variant) => !product.trackInventory || (variant.stock || 0) > 0) || null;
  const [variantId, setVariantId] = useState(firstAvailableVariant?._id || '');
  const selectedVariant = activeVariants.find((variant) => variant._id === variantId) || null;
  const key = cartLineKey(product._id, selectedVariant?._id);
  const cartItem = items.find((item) => item.key === key);
  const requiresVariant = (product.variants || []).length > 0;
  const soldOut =
    requiresVariant
      ? activeVariants.length === 0 ||
        (product.trackInventory && activeVariants.every((variant) => (variant.stock || 0) <= 0))
      : product.trackInventory && (product.stock || 0) <= 0;
  const canAdd = !soldOut && (!requiresVariant || selectedVariant);
  const maxAvailable = product.trackInventory
    ? selectedVariant
      ? selectedVariant.stock || 0
      : product.stock || 0
    : 25;
  const maxCartQuantity = product.trackInventory ? Math.max(0, Math.min(25, maxAvailable)) : 25;
  const atMaximum = Boolean(cartItem && cartItem.quantity >= maxCartQuantity);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
      <Link to={`/store/product/${product.slug}`} className="relative block aspect-square overflow-hidden bg-slate-100 dark:bg-slate-800">
        {product.images?.[0]?.url ? (
          <img
            src={product.images[0].url}
            alt={product.images[0].alt || product.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-6xl" aria-hidden>🎁</div>
        )}
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {product.featured && (
            <span className="rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-slate-950">
              Featured
            </span>
          )}
          {soldOut && (
            <span className="rounded-full bg-slate-950/85 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white">
              Sold out
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
          {product.category}
        </p>
        <Link to={`/store/product/${product.slug}`} className="mt-1">
          <h2 className="line-clamp-2 text-lg font-bold text-slate-900 dark:text-white group-hover:text-amber-700 dark:group-hover:text-amber-400">
            {product.name}
          </h2>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {product.shortDescription || product.description || 'Exclusive WE R GAME reward.'}
        </p>
        {requiresVariant && (
          <label className="mt-4 block">
            <span className="sr-only">Choose product option</span>
            <select
              value={variantId}
              onChange={(event) => setVariantId(event.target.value)}
              className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-800 dark:text-slate-100"
            >
              <option value="">Choose an option</option>
              {activeVariants.map((variant) => (
                <option
                  key={variant._id}
                  value={variant._id}
                  disabled={product.trackInventory && (variant.stock || 0) <= 0}
                >
                  {variant.name}
                  {product.trackInventory ? ` — ${(variant.stock || 0) > 0 ? `${variant.stock} left` : 'sold out'}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="mt-auto flex items-end justify-between gap-3 pt-5">
          <div>
            {product.compareAtGolden > product.priceGolden && (
              <p className="text-xs text-slate-400 line-through">{product.compareAtGolden} golden</p>
            )}
            <GoldenAmount amount={product.priceGolden} size="lg" />
          </div>
          <div className="flex items-center gap-2">
            {cartItem ? (
              <Link
                to="/store/cart"
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400 text-lg text-slate-950 hover:bg-amber-500"
                aria-label={`View cart with ${cartItem.quantity} ${product.name}`}
                title="View cart"
              >
                🛒
                <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-slate-950 px-1 text-center text-[10px] font-black leading-5 text-white">
                  {cartItem.quantity}
                </span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => canAdd && addItem(product, selectedVariant, 1)}
                disabled={!canAdd}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400 text-lg text-slate-950 hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={requiresVariant && !selectedVariant ? 'Choose an option before adding to cart' : `Add ${product.name} to cart`}
                title={requiresVariant && !selectedVariant ? 'Choose an option' : 'Add to cart'}
              >
                🛒
              </button>
            )}
            <Link
              to={`/store/product/${product.slug}`}
              className="rounded-xl border border-slate-200 dark:border-white/10 px-3 py-2 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
            >
              View
            </Link>
          </div>
        </div>
        {cartItem && (
          <div className="mt-3 border-t border-slate-100 dark:border-white/5 pt-3">
            <CartQuantityControls
              quantity={cartItem.quantity}
              onDecrease={() => updateQuantity(cartItem.key, cartItem.quantity - 1)}
              onIncrease={() => addItem(product, selectedVariant, 1)}
              disableIncrease={atMaximum}
              compact
              className="w-full"
            />
          </div>
        )}
      </div>
    </article>
  );
}

export function StoreSkeletonGrid({ count = 8 }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900">
          <div className="aspect-square animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="space-y-3 p-5">
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-5 w-4/5 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function OrderStatusBadge({ status }) {
  const classes = {
    placed: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    processing: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
    packed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300',
    shipped: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
    delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold capitalize ${classes[status] || classes.placed}`}>
      {status}
    </span>
  );
}
